import { Builder } from '@builder.io/react';
import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { backgroundColorInput, componentLinkIdInput } from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  cleanBuilderStringInput,
  getBuilderString,
  getCleanInput,
  isBuilderStringPresent,
  SanitizeMode,
} from '@/shared/text';
import { BuilderString, SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { ActionVariant } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import sharedStyle from '../shared.module.scss';
import styles from './StepBox.module.scss';

export interface StepBoxStep {
  title: BuilderString;
  description: BuilderString;
}

export type StepBoxProps = SharedBuilderComponent & {
  componentLinkId?: string;
  title?: BuilderString;
  description?: BuilderString;
  steps: StepBoxStep[];
  locale: string;
};

export const StepBox: FC<StepBoxProps> = ({
  componentLinkId,
  title,
  description,
  steps = [],
  locale,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
}) => {
  const blockLocale = locale || builderState?.context.locale || 'en';
  const sectionLabel =
    cleanBuilderStringInput(getBuilderString(title, blockLocale), true) ||
    'Steps';

  return (
    <section
      {...attributes}
      id={componentLinkId}
      aria-label={sectionLabel}
      className={cn(
        sharedStyle.section,
        styles.root,
        getBuilderIOClassName(attributes),
        getBuilderSpacingClass(builderBlock, builderState),
        backgroundColorClassName
      )}
    >
      <div className="u-wrapper">
        <ComponentHeader
          title={title}
          description={description}
          allowLists
          fullWidth
          action={{ label: '', url: '' }}
          actionVariant={ActionVariant.NONE}
          locale={blockLocale}
          headerClassName="u-m-b-xl u-m-b-lg-xxl-6"
        >
          <ol className={styles.steps} aria-label={`${sectionLabel} steps`}>
            {steps.map((step, index) => (
              <li className={styles.step} key={index}>
                <span
                  className={cn(
                    styles.stepNumber,
                    'tw:rounded-full tw:border tw:border-neutral-secondary tw:bg-neutral-tertiary tw:text-label-l tw:text-brand-primary'
                  )}
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div
                  className={cn(
                    'tw:bg-neutral-tertiary u-p-xl u-p-lg-xxl-4',
                    styles.card
                  )}
                >
                  <h3 className="tw:text-h5">
                    {parse(
                      getCleanInput(
                        blockLocale,
                        SanitizeMode.SINGLE_LINE,
                        step.title
                      )
                    )}
                  </h3>
                  {isBuilderStringPresent(step.description, blockLocale) && (
                    <div
                      className={cn(
                        'tw:text-body tw:text-neutral-secondary u-m-t-xs',
                        styles.description,
                        styles.descriptionWithLists
                      )}
                    >
                      {parse(
                        getCleanInput(
                          blockLocale,
                          SanitizeMode.MULTIPLE_LINE,
                          step.description,
                          { allowLists: true }
                        )
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </ComponentHeader>
      </div>
    </section>
  );
};

export const NAME = 'StepBox';

const DEFAULT_STEPS: StepBoxStep[] = [
  {
    title: 'Upload your lots',
    description: 'Submit your objects using the regular auction instructions.',
  },
  {
    title: 'Schedule the auction',
    description: 'Catawiki reviews and schedules your auction.',
  },
  {
    title: 'Prepare the livestream',
    description: 'Your livestream campaign is created after publication.',
  },
  {
    title: 'Go live alongside the auction',
    description: 'Show your lots and interact with buyers in real time.',
  },
  {
    title: 'Review your first stream',
    description: 'Use the moderation feedback to plan your next broadcast.',
  },
];

export function componentRegister() {
  Builder.registerComponent(StepBox, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'CatawikiEditor',
        defaultValue: 'Auction flow',
        localized: true,
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue: 'How the auction and livestream work together.',
        localized: true,
      },
      {
        name: 'steps',
        friendlyName: 'Steps',
        type: 'list',
        defaultValue: DEFAULT_STEPS,
        subFields: [
          {
            name: 'title',
            friendlyName: 'Step title',
            type: 'CatawikiPlainEditor',
            required: true,
            localized: true,
          },
          {
            name: 'description',
            friendlyName: 'Description',
            type: 'CatawikiEditor',
            localized: true,
          },
        ],
      },
      backgroundColorInput(COLOR_NAME_WHITE),
    ],
    noWrap: true,
  });
  return NAME;
}

export function dataFetcher(locale: string) {
  return async () => ({ locale });
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
